import React from 'react';

const variantStyles = {
  violet: {
    border: 'border-[#7B61FF]/30',
    bg: 'bg-[#18181B]',
    eyebrow: 'text-[#7B61FF]',
    dot: 'bg-[#7B61FF]',
    glow: 'from-[#7B61FF]/15',
  },
  green: {
    border: 'border-[#10B981]/30',
    bg: 'bg-[#0d1a14]',
    eyebrow: 'text-[#10B981]',
    dot: 'bg-[#10B981]',
    glow: 'from-[#10B981]/15',
  },
  amber: {
    border: 'border-amber-500/35',
    bg: 'bg-[#1a1700]',
    eyebrow: 'text-amber-400',
    dot: 'bg-amber-400',
    glow: 'from-amber-500/15',
  },
  red: {
    border: 'border-red-400/35',
    bg: 'bg-[#1a0e0e]',
    eyebrow: 'text-red-300',
    dot: 'bg-red-400',
    glow: 'from-red-500/15',
  },
};

export const Callout = ({ eyebrow, title, children, variant = 'violet' }) => {
  const s = variantStyles[variant] || variantStyles.violet;
  return (
    <aside className={`not-prose my-10 rounded-2xl border ${s.border} ${s.bg} p-7 relative overflow-hidden`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${s.glow} via-transparent to-transparent pointer-events-none`} />
      <div className="relative z-10">
        {eyebrow && <p className={`text-xs font-data uppercase tracking-[0.25em] ${s.eyebrow} mb-3`}>{eyebrow}</p>}
        {title && <h3 className="font-heading text-2xl font-bold text-[#F0EFF4] mb-3">{title}</h3>}
        <div className="text-[#F0EFF4]/70 leading-relaxed space-y-3">{children}</div>
      </div>
    </aside>
  );
};

export const Takeaway = ({ title = 'Takeaway', items = [], variant = 'green' }) => {
  const s = variantStyles[variant] || variantStyles.green;
  return (
    <div className={`not-prose my-10 rounded-2xl border ${s.border} ${s.bg} p-7`}>
      <p className={`text-xs font-data uppercase tracking-[0.25em] ${s.eyebrow} mb-5`}>{title}</p>
      <ul className="space-y-3 m-0 p-0 list-none">
        {items.map(item => (
          <li key={item} className="flex items-start gap-3 text-[#F0EFF4]/70 leading-relaxed">
            <span className={`mt-2 h-2 w-2 rounded-full ${s.dot} shrink-0`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Checklist = ({ title, items, variant = 'green' }) => {
  const s = variantStyles[variant] || variantStyles.green;
  return (
    <div className={`not-prose my-10 rounded-2xl border ${s.border} ${s.bg} p-7`}>
      <p className={`text-xs font-data uppercase tracking-[0.25em] ${s.eyebrow} mb-5`}>{title}</p>
      <ul className="space-y-3 m-0 p-0 list-none">
        {items.map(item => (
          <li key={item} className="flex items-start gap-3 text-[#F0EFF4]/70 leading-relaxed">
            <span className={`mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full border ${s.border} ${s.eyebrow} text-xs shrink-0`}>✓</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const StepCard = ({ number, title, children, variant = 'violet' }) => {
  const s = variantStyles[variant] || variantStyles.violet;
  return (
    <li className="rounded-2xl border border-[#F0EFF4]/10 bg-[#18181B] p-5 flex gap-4">
      <span className={`shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full border ${s.border} ${s.eyebrow} font-data text-sm font-bold`}>
        {number}
      </span>
      <div>
        <h3 className="font-heading text-lg font-bold text-[#F0EFF4] mb-2">{title}</h3>
        <div className="text-sm text-[#F0EFF4]/65 leading-relaxed">{children}</div>
      </div>
    </li>
  );
};

export const StepCardList = ({ title, intro, steps, variant = 'violet' }) => (
  <section className="not-prose my-12 rounded-[2rem] border border-[#F0EFF4]/10 bg-[#0A0A14]/70 p-6 md:p-8">
    {title && <p className="text-xs font-data uppercase tracking-[0.25em] text-[#10B981] mb-3">{title}</p>}
    {intro && <p className="text-[#F0EFF4]/65 leading-relaxed mb-6 max-w-3xl">{intro}</p>}
    <ol className="grid gap-4 m-0 p-0 list-none">
      {steps.map((step, index) => (
        <StepCard key={`${step.title}-${index}`} number={index + 1} title={step.title} variant={variant}>
          {step.body}
        </StepCard>
      ))}
    </ol>
  </section>
);

export const VideoEmbed = ({ title, youtubeId }) => (
  <figure className="not-prose my-14 rounded-[2rem] overflow-hidden border border-[#7B61FF]/30 bg-[#18181B] shadow-[0_0_40px_rgba(123,97,255,0.16)]">
    <div className="aspect-video w-full bg-[#05050A]">
      <iframe
        className="h-full w-full"
        src={`https://www.youtube.com/embed/${youtubeId}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </div>
    <figcaption className="px-6 py-4 text-sm text-[#F0EFF4]/55 font-data border-t border-[#F0EFF4]/10">
      {title}
    </figcaption>
  </figure>
);
